import React from 'react';
import fhirpath from 'fhirpath';

import { buildFormType } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.helpers';

import { usePrescricaoLivreContext } from '../../providers/PrescricaoLivreProvider';
import { ItemComplementarState, ItemComplementarUIState } from '../../models';
import { itemToUIState } from '../../helpers/parsers';
import ItemComplementarGroup from '../ItemComplementarGroup';
import FormDefaults from '../../models/FormDefaults';

const itemForm = buildFormType('encaminhamento', FormDefaults.Encaminhamento);

const getTitle = (item: ItemComplementarState) => {
  return fhirpath.evaluate(
    item.resource,
    'ServiceRequest.code.text | ServiceRequest.code.coding.display',
  )[0];
};

const getDescription = (item: ItemComplementarState) => {
  const reasonCode = fhirpath.evaluate(
    item.resource,
    'ServiceRequest.reasonCode.text | ServiceRequest.reasonCode.coding.display',
  );
  if (reasonCode.length) {
    return reasonCode[0];
  }

  return '';
};

const PrescricoesLivreEncaminhamento: React.FC = () => {
  const {
    mode,
    getItemsByType,
    getItemById,
    updateItemState,
    deleteItem,
    updateItem,
  } = usePrescricaoLivreContext();

  const encaminhamentos = React.useMemo(() => {
    const items = getItemsByType('encaminhamento');

    return items.map<ItemComplementarUIState>(item =>
      itemToUIState(item, {
        title: getTitle(item),
        description: getDescription(item),
      }),
    );
  }, [getItemsByType]);

  const canEditOrDelete = React.useMemo(() => mode === 'active', [mode]);

  const handleUpdate = React.useCallback(
    ({ key: updatingID, resource }: ItemComplementarUIState) => {
      const { itemState, type = 'encaminhamento' } =
        getItemById(updatingID) || {};

      updateItem({
        id: updatingID,
        type,
        resource,
        itemState,
      });
    },
    [getItemById, updateItem],
  );

  const handleItemEditCancel = React.useCallback(
    ({ key: id }: ItemComplementarUIState) => {
      const item = getItemById(id);

      // Encaminhamentos adicionados via frontend (ao invés de carregados da API)
      // devem ser excluídos se o usuário cancelar sua primeira edição
      if (item?.itemState?.isAdding) {
        deleteItem({ id });
      }
    },
    [deleteItem, getItemById],
  );

  if (encaminhamentos.length) {
    return (
      <ItemComplementarGroup
        tipo="encaminhamento"
        items={encaminhamentos}
        itemForm={itemForm}
        onChangeItem={handleUpdate}
        onDeleteItem={({ key: id }) => deleteItem({ id })}
        onChangeItemCancel={handleItemEditCancel}
        onItemDisplayModeChange={(item, display) =>
          updateItemState({ id: item.key }, { isEditing: display === 'form' })
        }
        hasItemDeletePermission={canEditOrDelete}
        hasItemChangePermission={canEditOrDelete}
      />
    );
  }

  return <></>;
};

export default PrescricoesLivreEncaminhamento;
